import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import RoleSelection from './components/auth/RoleSelection';
import TherapistDashboard from './components/dashboard/TherapistDashboard';
import PatientDashboard from './components/dashboard/PatientDashboard';
import PublicBooking from './components/booking/PublicBooking';
import LandingPage from './components/LandingPage';
import AboutUs from './components/AboutUs';
import TermsAndConditions from './components/TermsAndConditions';
import DesignSystemDemo from './components/design-system/DesignSystemDemo';
import ErrorBoundary from './components/ErrorBoundary';
import { Spinner } from './components/design-system';
import usePWAStatus from './hooks/usePWAStatus';
import analyticsService from './services/analyticsService';
import './index.css';

// Note: Auth initialization is now handled by AuthContext via authService
// This centralizes all auth-related code in one place

// Role-based redirect component - handles redirects based on user role
// React Router's Navigate component handles multiple renders gracefully
// Note: Demo mode is handled by route protection at /dashboard/patient, not here
const RoleBasedRedirect = ({ userRole }) => {
  if (userRole?.role === 'therapist') {
    return <Navigate to="/dashboard/therapist" replace />;
  } else {
    return <Navigate to="/dashboard/patient" replace />;
  }
};

const AppContent = () => {
  const { user, loading, authReady, isUserAuthenticated, isDemoMode, userInfo, refreshUserInfo } = useAuth();
  const { isPWA, isLoading: pwaLoading } = usePWAStatus();
  const location = useLocation();
  const [error, setError] = useState(null);
  
  // Helper to check demo mode from sessionStorage directly (for route protection)
  // This ensures demo mode is available even if state hasn't updated yet
  const checkDemoMode = () => {
    try {
      return sessionStorage.getItem('theraengage_demo_mode') === 'true';
    } catch (error) {
      return false;
    }
  };

  // Derive userRole from userInfo (no separate API call needed)
  // get-current-user already returns role, so we can compute hasRole from it
  const userRole = useMemo(() => {
    if (!userInfo) {
      return null;
    }
    const hasRole = !!userInfo.role && userInfo.role !== null;
    return {
      hasRole,
      role: userInfo.role || null,
      needsRoleSelection: userInfo.needsRoleSelection || !hasRole
    };
  }, [userInfo]);

  // Function to trigger user info refresh (after role selection)
  const triggerRoleCheck = useCallback(async () => {
    try {
      await refreshUserInfo();
    } catch (error) {
      console.error('Error refreshing user info after role selection:', error);
      setError('Failed to refresh user information. Please refresh the page.');
    }
  }, [refreshUserInfo]);

  // Initialize analytics on app load (available for all pages, including landing page)
  useEffect(() => {
    // Initialize analytics once, regardless of user authentication
    if (!analyticsService.isInitialized) {
      try {
        analyticsService.init({
          provider: process.env.REACT_APP_ANALYTICS_PROVIDER || 'posthog',
          config: {
            apiKey: process.env.REACT_APP_ANALYTICS_KEY,
            apiHost: process.env.REACT_APP_ANALYTICS_HOST
          }
        });
      } catch (error) {
        // Don't break the app if analytics fails
        console.error('Analytics initialization error:', error);
      }
    }
  }, []); // Run once on mount

  // Identify user when they log in
  useEffect(() => {
    if (user && userInfo && analyticsService.isInitialized) {
      try {
        // Wait a bit for PostHog to initialize before identifying
        // PostHog will queue events automatically, but this ensures better reliability
        const identifyUser = () => {
          // Check if initialized, or wait a bit more
          if (analyticsService.isInitialized) {
            analyticsService.identify(userInfo.userId, {
              role: userInfo.role
            });
          } else {
            // Retry after a short delay if not initialized yet
            setTimeout(identifyUser, 100);
          }
        };
        
        // Start the identify process after a small initial delay
        setTimeout(identifyUser, 200);
      } catch (error) {
        // Don't break the app if analytics fails
        console.error('Analytics identify error:', error);
      }
    }
  }, [user, userInfo]);

  // Track page views on route changes
  useEffect(() => {
    if (user && userInfo && analyticsService.isInitialized) {
      try {
        analyticsService.page(location.pathname, {
          role: userInfo.role,
          isDemoMode: isDemoMode || false
        });
      } catch (error) {
        // Don't break app if page tracking fails
        console.error('Page tracking error:', error);
      }
    }
  }, [location.pathname, user, userInfo, isDemoMode]);

  // Flush analytics events when app comes back online (important for PWAs)
  useEffect(() => {
    const handleOnline = () => {
      if (analyticsService.isInitialized) {
        try {
          analyticsService.flush();
          if (process.env.NODE_ENV === 'development') {
            console.log('📡 App came back online - flushing queued analytics events');
          }
        } catch (error) {
          console.error('Error flushing analytics on online event:', error);
        }
      }
    };

    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  if (loading || pwaLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="xxl" />
          <p className="mt-4 text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }
  
  // Wait for userInfo to be loaded before allowing route access
  // This ensures components have userInfo available when they render
  if (user && !userInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="large" className="mx-auto mb-4" />
          <p className="text-gray-600">Loading user information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              triggerRoleCheck();
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('🚀 App.js Routing Decision:', {
      isPWA,
      pwaLoading,
      user: !!user,
      path: window.location.pathname
    });
    
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/design-system" element={<DesignSystemDemo />} />
        <Route path="*" element={
          isPWA ? <Navigate to="/login" replace /> : <LandingPage />
        } />
      </Routes>
    );
  }

  // If user doesn't have a role yet, show role selection
  // userRole is derived from userInfo, so it's ready when userInfo is loaded
  if (userInfo && !userRole?.hasRole) {
    return <RoleSelection onRoleSelected={triggerRoleCheck} />;
  }

  // Role-based routing
  return (
    <>
      <Routes>
        {/* Public booking page */}
        <Route path="/book/:therapistId" element={<PublicBooking />} />
        
        {/* About Us */}
        <Route path="/about" element={<AboutUs />} />
        
        {/* Terms and Conditions */}
        <Route path="/terms" element={<TermsAndConditions />} />
        
        {/* Design system demo */}
        <Route path="/design-system" element={<DesignSystemDemo />} />
        
        {/* Login page */}
        <Route path="/login" element={<Login />} />
        
        {/* Role-based dashboards */}
        <Route 
          path="/dashboard/therapist" 
          element={
            userRole.role === 'therapist' ? 
            <TherapistDashboard /> : 
            <Navigate to="/dashboard/patient" replace />
          } 
        />
        
        {/* Patient detail page for therapists */}
        <Route 
          path="/dashboard/therapist/patient/:patientId" 
          element={
            userRole.role === 'therapist' ? 
            <TherapistDashboard /> : 
            <Navigate to="/dashboard/patient" replace />
          } 
        />
        
        {/* Other therapist dashboard sections */}
        <Route 
          path="/dashboard/therapist/availability" 
          element={
            userRole.role === 'therapist' ? 
            <TherapistDashboard /> : 
            <Navigate to="/dashboard/patient" replace />
          } 
        />
        
        <Route 
          path="/dashboard/therapist/bookings" 
          element={
            userRole.role === 'therapist' ? 
            <TherapistDashboard /> : 
            <Navigate to="/dashboard/patient" replace />
          } 
        />
        
        <Route 
          path="/dashboard/therapist/practice" 
          element={
            userRole.role === 'therapist' ? 
            <TherapistDashboard /> : 
            <Navigate to="/dashboard/patient" replace />
          } 
        />
        
        <Route 
          path="/dashboard/therapist/chat" 
          element={
            userRole.role === 'therapist' ? 
            <TherapistDashboard /> : 
            <Navigate to="/dashboard/patient" replace />
          } 
        />
        
        <Route 
          path="/dashboard/therapist/analytics" 
          element={
            userRole.role === 'therapist' ? 
            <TherapistDashboard /> : 
            <Navigate to="/dashboard/patient" replace />
          } 
        />
        
        <Route 
          path="/dashboard/therapist/settings" 
          element={
            userRole.role === 'therapist' ? 
            <TherapistDashboard /> : 
            <Navigate to="/dashboard/patient" replace />
          } 
        />
        
        <Route 
          path="/dashboard/therapist/notifications" 
          element={
            userRole.role === 'therapist' ? 
            <TherapistDashboard /> : 
            <Navigate to="/dashboard/patient" replace />
          } 
        />
        
        <Route 
          path="/dashboard/therapist/help" 
          element={
            userRole.role === 'therapist' ? 
            <TherapistDashboard /> : 
            <Navigate to="/dashboard/patient" replace />
          } 
        />
        
        <Route 
          path="/dashboard/patient" 
          element={
            (() => {
              // Allow if patient OR therapist in demo mode
              // Check both state and sessionStorage to handle race conditions
              const demoModeActive = isDemoMode || checkDemoMode();
              if (userRole.role === 'patient' || (userRole.role === 'therapist' && demoModeActive)) {
                return <PatientDashboard />;
              }
              return <Navigate to="/dashboard/therapist" replace />;
            })()
          } 
        />
        
        {/* Default redirect based on role - "/" route handles root path */}
        <Route 
          path="/" 
          element={<RoleBasedRedirect userRole={userRole} />}
        />
        
        {/* Catch all - redirect unknown paths to root, which will redirect appropriately */}
        <Route 
          path="*" 
          element={<Navigate to="/" replace />}
        />
      </Routes>
      

    </>
  );
};

function App() {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  if (!domain || !clientId || !audience) {
    return (
      <ErrorBoundary>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Configuration Error</h2>
            <p className="text-gray-600">
              Auth0 environment variables are not configured. Please set:
              <br />
              REACT_APP_AUTH0_DOMAIN, REACT_APP_AUTH0_CLIENT_ID, REACT_APP_AUTH0_AUDIENCE
            </p>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: audience,
        }}
        useRefreshTokens={true}
        cacheLocation="localstorage"
        onRedirectCallback={(appState) => {
          // Handle redirect callback
          // Check for errors in URL
          const urlParams = new URLSearchParams(window.location.search);
          const error = urlParams.get('error');
          const errorDescription = urlParams.get('error_description');
          
          if (error) {
            console.error('Auth0 redirect error:', error, errorDescription);
            // Error will be handled by AuthContext
          }
        }}
      >
        <AuthProvider>
          <div className="App">
            <AppContent />
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10B981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 4000,
                  iconTheme: {
                    primary: '#EF4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
        </AuthProvider>
      </Auth0Provider>
    </ErrorBoundary>
  );
}

export default App; 